import { Button } from "../../../shared";
import { Box, Typography, Chip } from "@mui/joy";
import { ChevronRight, CheckCircle2 } from "lucide-react";

import { useSelectedSupplier } from "../../../components/WorkEstimation/components/hooks";

export function GFCSelectionButton(params: any) {
    const { colDef, data } = params;
    const purchaseStrategy = params.purchaseStrategy || "single";

    const { selectedSupplier, setSelectedSupplier } = useSelectedSupplier();

    const handleClick = () => {
        setSelectedSupplier({ name: colDef.field, id: colDef.field });
    };

    const isSelected = selectedSupplier?.name === colDef.field;
    const isBest = data?.bestTotalSupplier === colDef.field;

    return (
        <Box height={"100%"} display={"flex"} alignItems={"center"} width="100%">
            <Button
                size={"sm"}
                variant={isSelected ? "solid" : "outlined"}
                color={isSelected ? "primary" : isBest ? "success" : "neutral"}
                endDecorator={<ChevronRight size={14} />}
                onClick={handleClick}
                sx={{
                    height: "100%",
                    width: "100%",
                    borderRadius: "0",
                    py: 0.5,
                    border: "none",
                    px: 1.5,
                    fontWeight: 500,
                    fontSize: "0.75rem",
                    justifyContent: "flex-start",
                }}
            >
                {isSelected ? "Selected" : "Select"}
            </Button>
        </Box>
    );
}

export function GFCPrice(params: any) {
    const { data } = params;
    const isBest = params.isBest || false;
    const purchaseStrategy = params.purchaseStrategy || "single";
    const isSelected = params.isSelected || false;
    const onItemSupplierSelect = params.onItemSupplierSelect;
    const productName = params.productName;
    const supplierName = params.supplierName;

    const handleClick = () => {
        if (purchaseStrategy === "multiple" && onItemSupplierSelect && productName && supplierName) {
            onItemSupplierSelect(productName, supplierName);
        }
    };

    // Handle footer row (totals)
    if (data?.isFooter) {
        const total = data.totals?.[params.colDef.field] || 0;
        return (
            <Box display={"flex"} alignItems={"center"} flex={1} height={"100%"} gap={0.75} px={1.5}>
                <Typography
                    level="body-xs"
                    sx={{
                        fontWeight: 600,
                        color: isBest ? "success.plainColor" : "text.primary",
                    }}
                >
                    ${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                {isBest && (
                    <Chip
                        size="sm"
                        variant="soft"
                        color="success"
                        startDecorator={<CheckCircle2 size={10} />}
                        sx={{ fontSize: "0.65rem", py: 0.125, px: 0.5, minHeight: "auto" }}
                    >
                        Best
                    </Chip>
                )}
            </Box>
        );
    }

    // Handle missing price or quantity
    if (!params.value || params.value === 0 || !data?.Quantity) {
        return (
            <Box display={"flex"} alignItems={"center"} flex={1} height={"100%"} px={1.5}>
                <Typography level="body-xs" color="neutral">
                    N/A
                </Typography>
            </Box>
        );
    }

    const unitPrice = Number(params.value);
    const quantity = Number(data.Quantity);
    const totalPrice = unitPrice * quantity;

    const isClickable = purchaseStrategy === "multiple" && onItemSupplierSelect && productName && supplierName;

    return (
        <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"center"}
            flex={1}
            height={"100%"}
            gap={0.25}
            px={1.5}
            onClick={isClickable ? handleClick : undefined}
            sx={{
                bgcolor: isSelected
                    ? "primary.softBg"
                    : isBest
                    ? "success.softBg"
                    : isClickable
                    ? "background.level1"
                    : "transparent",
                cursor: isClickable ? "pointer" : "default",
                border: isSelected ? "2px solid" : "none",
                borderColor: isSelected ? "primary.500" : "transparent",
                borderRadius: isClickable ? "sm" : "none",
                transition: isClickable ? "all 0.2s ease" : "none",
                "&:hover": isClickable
                    ? {
                          bgcolor: isSelected ? "primary.softBg" : "background.level2",
                          borderColor: isSelected ? "primary.500" : "primary.300",
                      }
                    : {},
            }}
        >
            <Box display="flex" alignItems="center" gap={0.5}>
                <Typography
                    level="body-xs"
                    sx={{
                        fontWeight: 600,
                        color: isSelected ? "primary.plainColor" : isBest ? "success.plainColor" : "text.primary",
                        lineHeight: 1.2,
                    }}
                >
                    ${totalPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                {isSelected && <CheckCircle2 size={12} color="var(--joy-palette-primary-500)" />}
                {!isSelected && isBest && <CheckCircle2 size={12} />}
            </Box>
            <Typography level="body-xs" sx={{ color: "text.tertiary", fontSize: "0.7rem", lineHeight: 1.2 }}>
                ${unitPrice.toFixed(2)} × {quantity}
            </Typography>
        </Box>
    );
}
